import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Key, LogOut, Plus } from 'lucide-react';
import { CustomIcon } from '../CustomIcon';
import ThemeToggle from '../Helper/ThemeToggle';
import NotificationsDropdown from './notifications-dropdown';
import { SidebarTrigger } from '@/components/ui/sidebar';
import Breadcrumb from '../Breadcrumb';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import CashbackModal from '@/components/ModalsProvider/cashback/cashback-modal';
import { useRootSelector } from '@/hooks/selector.hook';
import { checkActionPermission } from '@/utils/permission';
import { useAuth } from '@/hooks/auth.hook';
import { useMyAccounts } from '@/hooks/my-accounts.hook';
import { useEntityContext } from '@/hooks/entity-context.hook';
import ChangePasswordDialog from '@/components/ModalsProvider/change-password.modal';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const Header = () => {
  const [isCashbackModalOpen, setIsCashbackModalOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [openingBusinessAccount, setOpeningBusinessAccount] = useState(false);
  const globalActions = useRootSelector((state) => state.permission.globalActions);
  const user = useRootSelector((state) => state.auth.user);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { primary: businessAccount, accounts, setActive } = useMyAccounts();
  const { currentApplication, startNewEntityApplication } = useEntityContext();
  const hasOpenBusinessApplication = Boolean(
    currentApplication && currentApplication.status !== 'Approved' && !currentApplication.entityId,
  );

  const checkPermissions = useCallback(
    (actionIds: string | string[] | undefined) => {
      if (!actionIds) return true;
      const ids = Array.isArray(actionIds) ? actionIds : [actionIds];
      return ids.some((id) => checkActionPermission(globalActions, id));
    },
    [globalActions]
  );

  const handleOpenCashbackModal = () => {
    setIsCashbackModalOpen(true);
  };

  const handleCloseCashbackModal = () => {
    setIsCashbackModalOpen(false);
  };

  const handleOpenBusinessAccount = async () => {
    if (openingBusinessAccount) return;
    setOpeningBusinessAccount(true);
    try {
      await startNewEntityApplication();
      navigate('/kyc/submit');
    } finally {
      setOpeningBusinessAccount(false);
    }
  };

  return (
    <>
      <header className="relative flex items-center justify-between border-b border-primary bg-transparent px-[0.8rem] py-[1.2rem] lg:h-[8rem] lg:px-[3rem] lg:py-0">
        {/* Left side - 50% width on mobile */}
        <div className="flex min-w-0 flex-1 items-center space-x-4 lg:flex-none">
          <SidebarTrigger className="!h-[2rem] !w-[2rem] !p-0 text-[#1D1D1D] dark:text-white [&>svg]:!h-[2rem] [&>svg]:!w-[2rem]" />
          <Breadcrumb />
        </div>

        {/* Mobile Logo - centered */}
        {/* <div className="logo hidden lg:flex">
          <img className="w-[9rem]" src={Logo} alt="NezenX" />
        </div> */}

        {/* Right side - 50% width on mobile */}
        <div className="flex shrink-0 items-center justify-end gap-[0.8rem] md:gap-[1.2rem] lg:gap-[1.5rem]">
          {checkPermissions("view_cashback") && <Button
            onClick={handleOpenCashbackModal}
            className="fixed bottom-[8rem] z-[50] hidden h-[6rem] w-[6rem] rounded-[1.8rem] bg-[#57E8BA] p-[1.2rem] text-[#121212] lg:static lg:flex lg:h-[4.8rem] lg:w-auto lg:rounded-[1.8rem] lg:text-[1.6rem] lg:font-medium"
          >
            <CustomIcon type="cashback-sidebar" className="w-[2.4rem] h-[2.4rem]" />
            <span className='hidden lg:block'>Cashback</span>
          </Button>}

          {/* Notifications */}
          <NotificationsDropdown />

          <Button size={"sz38"} className="h-[3.8rem] w-[3.8rem] gap-0 p-0 lg:h-[3.8rem] lg:w-auto lg:gap-[0.8rem] lg:px-[1rem]">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="!h-[2rem] !w-[2rem] shrink-0">
              <path d="M9.5 13.75C9.5 14.72 10.25 15.5 11.17 15.5H13.05C13.85 15.5 14.5 14.82 14.5 13.97C14.5 13.06 14.1 12.73 13.51 12.52L10.5 11.47C9.91 11.26 9.51001 10.94 9.51001 10.02C9.51001 9.17999 10.16 8.48999 10.96 8.48999H12.84C13.76 8.48999 14.51 9.26999 14.51 10.24" stroke="#1D1D1D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M12 7.5V16.5" stroke="#1D1D1D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M22 12C22 17.52 17.52 22 12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12 2" stroke="#1D1D1D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M22 6V2H18" stroke="#1D1D1D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M17 7L22 2" stroke="#1D1D1D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span className="hidden lg:inline">Withdraw Cashback</span>
          </Button>
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* eslint-disable-next-line no-constant-binary-expression */}
          {false && <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="second"
                type="button"
                className="flex cursor-pointer items-center gap-[0.8rem] rounded-[1rem] px-[0.4rem] py-[0.2rem] text-left"
                align="start"
                aria-label="Open user menu"
              >
                <Avatar className="h-[3rem] w-[3rem]">
                  <AvatarImage src={user?.avatar} alt={user?.email} />
                  <AvatarFallback className="bg-[#DDEDE8] text-[1.2rem] font-semibold text-[#26493E]">
                    {user?.firstName?.[0] || 'U'}{user?.lastName?.[0] || ''}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden min-w-[10rem] max-w-[15rem] text-left leading-tight lg:block">
                  <span className="block truncate font-normal text-[1.4rem] leading-[100%] tracking-[0] text-main">
                    {user?.firstName || 'User'} {user?.lastName || ''}
                  </span>
                  <span className="mt-[0.4rem] block truncate font-normal text-[1.2rem] leading-none tracking-[0] sub-text">
                    {businessAccount?.name || 'Account'}
                  </span>
                </span>
                <ChevronDown className="hidden h-[1.6rem] w-[1.6rem] text-main lg:block" strokeWidth={1.8} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[30rem] rounded-[1.4rem] border border-[#E9EDF0] bg-white p-[0.5rem] shadow-[0_0.6rem_1.8rem_rgba(16,24,40,0.1)] dark:border-transparent dark:bg-[#343434] dark:shadow-black/30">
              <DropdownMenuLabel className="px-[1.2rem] py-[0.9rem]">
                <div className="flex items-center gap-[0.9rem]">
                  <Avatar className="h-[3rem] w-[3rem]">
                    <AvatarImage src={user?.avatar} alt={user?.email} />
                    <AvatarFallback className="bg-[#DDEDE8] text-[#26493E]">
                      {user?.firstName?.[0] || 'U'}{user?.lastName?.[0] || ''}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="truncate text-[1.35rem] font-semibold text-main">
                      {user?.firstName || 'User'} {user?.lastName || ''}
                    </p>
                    <p className="mt-[0.25rem] truncate font-normal text-[1.1rem] leading-tight tracking-[0] sub-text">{user?.email || 'Signed in user'}</p>
                  </div>
                </div>
              </DropdownMenuLabel>
              {accounts.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="mx-[0.4rem] cursor-pointer rounded-[0.7rem] px-[0.8rem] py-[0.8rem] text-[1.25rem] focus:bg-[#F5F7F6] !data-[state=open]:!bg-[#F5F7F6] dark:text-white dark:focus:bg-[#3D4140] dark:!data-[state=open]:!bg-[#3D4140]">
                      <Avatar className="h-[2.8rem] w-[2.8rem]">
                        <AvatarImage src={user?.avatar} alt={user?.email} />
                        <AvatarFallback className="bg-[#DDEDE8] text-[1rem] text-[#26493E]">
                          {user?.firstName?.[0] || 'U'}{user?.lastName?.[0] || ''}
                        </AvatarFallback>
                      </Avatar>
                      <span className="flex min-w-0 flex-1 flex-col text-left">
                        <span className="truncate text-[1.05rem] font-semibold uppercase tracking-wide text-disable">Business account</span>
                        <span className="truncate font-medium">{businessAccount?.name || 'Account'}</span>
                      </span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent side="left" align="start" sideOffset={16} className="w-[25rem] rounded-[1.2rem] border border-[#E9EDF0] bg-white p-[0.5rem] shadow-[0_0.6rem_1.8rem_rgba(16,24,40,0.1)] dark:border-transparent dark:bg-[#343434]">
                      {accounts.map((account) => (
                        <DropdownMenuItem
                          key={account.id}
                          onClick={() => setActive(account.id)}
                          className="cursor-pointer gap-[0.8rem] rounded-[0.7rem] px-[1rem] py-[0.8rem] text-[1.25rem] focus:bg-[#F5F7F6] dark:text-white dark:focus:bg-[#3D4140]"
                        >
                          <span className="flex h-[2.4rem] w-[2.4rem] items-center justify-center rounded-full bg-[#DDEDE8] text-[1rem] text-[#26493E]">
                            {account.name?.[0] || 'A'}
                          </span>
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate font-medium">{account.name}</span>
                            <span className="truncate text-[1.05rem] text-disable">{account.role} · {account.code}</span>
                          </span>
                          {businessAccount?.id === account.id && <span className="text-[1.6rem] text-[#57E8BA]">✓</span>}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        disabled={openingBusinessAccount}
                        onSelect={(event) => {
                          event.preventDefault();
                          void handleOpenBusinessAccount();
                        }}
                        className="cursor-pointer gap-[0.8rem] rounded-[0.7rem] px-[1rem] py-[0.8rem] text-[1.25rem] font-medium text-[#087C59] focus:bg-[#F5F7F6] dark:text-[#57E8BA] dark:focus:bg-[#3D4140]"
                      >
                        {openingBusinessAccount ? <Skeleton className="h-[1.5rem] w-[1.5rem] rounded-full" /> : <Plus className="h-[1.5rem] w-[1.5rem]" />}
                        Add another account
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}
              {/* eslint-disable-next-line no-constant-binary-expression */}
              {false && accounts.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="px-[1.2rem] py-[0.6rem] text-[1.05rem] font-semibold uppercase tracking-wide text-disable">
                    Businesses
                  </DropdownMenuLabel>
                  {accounts.map((account) => (
                    <DropdownMenuItem
                      key={account.id}
                      onClick={() => setActive(account.id)}
                      className="cursor-pointer gap-[0.6rem] px-[1.2rem] py-[0.75rem] text-[1.25rem] focus:bg-transparent"
                    >
                      <span className="mr-[0.8rem] w-[1.4rem] text-[#57E8BA]">
                        {businessAccount?.id === account.id ? '✓' : ''}
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{account.name}</span>
                        <span className="truncate text-[1.1rem] text-disable">{account.role} · {account.code}</span>
                      </span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={openingBusinessAccount}
                    onSelect={(event) => {
                      event.preventDefault();
                      void handleOpenBusinessAccount();
                    }}
                    className="cursor-pointer gap-[0.6rem] px-[1.2rem] py-[0.9rem] text-[1.25rem] font-medium text-[#087C59] focus:bg-transparent dark:text-[#57E8BA] disabled:cursor-not-allowed"
                  >
                    {openingBusinessAccount ? (
                      <Skeleton className="mr-[0.8rem] h-[1.6rem] w-[1.6rem] rounded-full" />
                    ) : (
                      <Plus className="mr-[0.8rem] h-[1.6rem] w-[1.6rem]" />
                    )}
                    {hasOpenBusinessApplication ? 'Continue business application' : 'Open a new business account'}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setChangePasswordOpen(true)}
                className="cursor-pointer gap-[0.6rem] px-[1.2rem] py-[0.9rem] text-[1.25rem] hover:bg-[#F5F7F6] focus:bg-transparent dark:hover:bg-[#3D4140]"
              >
                <Key className="mr-[0.3rem] !h-[1.6rem] !w-[1.6rem]" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="cursor-pointer gap-[0.6rem] px-[1.2rem] py-[0.9rem] text-[1.25rem] text-red-600 hover:bg-[#FFF4F4] focus:bg-transparent dark:text-red-400 dark:hover:bg-[#4A3434]"
              >
                <LogOut className="mr-[0.3rem] !h-[1.6rem] !w-[1.6rem]" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>}
        </div>
      </header>

      {/* Cashback Modal */}

      <CashbackModal
        open={isCashbackModalOpen}
        closeModal={handleCloseCashbackModal}
      />
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </>
  );
};

export default Header;
